import { useState, type FC } from 'react'
import Link from 'next/link'

import { Text } from '@wartek-id/text-v1'
import { Pagination } from '@wartek-id/pagination'
import { Button } from '@wartek-id/button-v1'

import FilterUserRole from 'app/KinerjaDinasPage/components/FilterUserRole'
import { useApiGetPelaksanaanDinas } from 'api/kinerja/kepala-sekolah/dinas/pelaksanaan/detail'
import {
  TableBody,
  TableHeader,
  TableHeaderItem,
  TablePelaksanaanKinerja,
  TableRow,
  TableRowItem,
  TableShimmer,
} from 'app/KinerjaShared/components/TablePelaksanaanKinerja'
import BadgePerilakuKerja from 'app/KinerjaShared/components/BadgePerilakuKerja'
import BadgeObservationStatus from 'app/KinerjaShared/components/BadgeObservationStatus'
import {
  PENGELOLAAN_KINERJA_KADIN_PATH,
  PENGELOLAAN_KINERJA_KS_PENILAI_PATH,
} from 'app/KinerjaShared/constants/path'
import TableRowStates from 'app/KinerjaShared/components/TableRowStates'
import useToggleSdkContext from 'utils/useToogleSdk'

import type { PelaksanaanDinas } from 'types/kinerja-ks-pelaksanaan'
import type { KinerjaRoleType } from 'types/kinerja'

interface CardProgresPelaksanaanProps {}

const RenderCell: FC<{
  item: PelaksanaanDinas
  value: React.ReactElement
}> = ({ item, value }) => {
  // If the observation status is not 'PREPARATION', return the value.
  // And if the value is not available, return null.
  if (item?.observationStatus !== 'PREPARATION') return value || null

  // Otherwise, return '-'
  return <>-</>
}

const CardProgresPelaksanaan: FC<CardProgresPelaksanaanProps> = ({}) => {
  const [page, setPage] = useState<number>(1)
  const [activeFilter, setCurrentActiveFilter] =
    useState<KinerjaRoleType>('KS_DEFINITIF')

  const toggleValue = useToggleSdkContext()

  const {
    data: { data: pelaksanaan, meta: metas } = {},
    isLoading,
    isSuccess,
    isError,
    refetch,
  } = useApiGetPelaksanaanDinas(
    {
      page,
      pageSize: 10,
      role: activeFilter,
    },
    { enabled: true }
  )

  const isPelaksanaanError = !isLoading && isError
  const hasPelaksanaanData =
    !isPelaksanaanError && isSuccess && pelaksanaan?.length > 0
  const hasNoPelaksanaanData =
    !isPelaksanaanError && isSuccess && pelaksanaan?.length === 0

  const resetPage = () => {
    setPage(1)
  }

  const getCheckUrl = (item: typeof pelaksanaan[0]) => {
    if (item.role === 'Guru') {
      return `${PENGELOLAAN_KINERJA_KS_PENILAI_PATH}/penilaian-kinerja/${item?.npsn}/${item?.ptkId}`
    }

    return `${PENGELOLAAN_KINERJA_KADIN_PATH}/pelaksanaan/${item?.npsn}`
  }

  return (
    <div className="bg-white rounded-lg border border-subdued">
      <div className="p-6 pb-2 space-y-4">
        {/* Labels */}
        <div className="space-y-1">
          <Text variant="heading-md" className="!text-black">
            Daftar Pelaksanaan Kinerja
          </Text>
          <Text variant="body" color="subdued">
            Pastikan data sudah sesuai dan cek status Praktik Kinerja dan
            Perilaku Kerja.
          </Text>
        </div>

        {/* Filters */}
        <FilterUserRole
          isLoading={isLoading}
          activeFilter={activeFilter}
          onChange={(selected) => {
            if (selected !== activeFilter) setCurrentActiveFilter(selected)

            // Reset page number
            resetPage()
          }}
        />
      </div>
      {/* Table */}
      <TablePelaksanaanKinerja>
        <TableHeader>
          <TableHeaderItem>Nama</TableHeaderItem>
          <TableHeaderItem>Jabatan</TableHeaderItem>
          <TableHeaderItem>Satuan Pendidikan</TableHeaderItem>
          <TableHeaderItem>Tim Kerja</TableHeaderItem>
          <TableHeaderItem>Status Praktik Kinerja</TableHeaderItem>
          <TableHeaderItem>Status Perilaku Kerja</TableHeaderItem>
          <TableHeaderItem className="w-20" />
        </TableHeader>
        <TableBody>
          {/* If loading */}
          {isLoading && <TableShimmer columns={7} />}

          {/* If error or no data returned */}
          {(isPelaksanaanError || hasNoPelaksanaanData) && (
            <TableRowStates
              type={isPelaksanaanError ? 'ERROR' : 'EMPTY'}
              onClick={() => {
                // window.location.reload()
                refetch()
              }}
            />
          )}

          {/* If some data returned */}
          {hasPelaksanaanData &&
            pelaksanaan.map((item, idx) => {
              return (
                <TableRow key={`ks_${item.npsn}_${idx}`}>
                  <TableRowItem className="overflow-wrap-anywhere align-middle">
                    <Text variant="body" color="default">
                      {item?.name || ''}
                    </Text>
                  </TableRowItem>
                  <TableRowItem className="overflow-wrap-anywhere align-middle">
                    <Text variant="body" color="default">
                      {item?.role || ''}
                    </Text>
                  </TableRowItem>
                  <TableRowItem className="overflow-wrap-anywhere align-middle">
                    <Text variant="body" color="default">
                      {item?.schoolName || ''}
                    </Text>
                  </TableRowItem>
                  <TableRowItem className="overflow-wrap-anywhere align-middle">
                    <Text variant="body" color="default">
                      {item?.schoolSupervisor || ''}
                    </Text>
                  </TableRowItem>
                  <TableRowItem className="overflow-wrap-anywhere align-middle">
                    <RenderCell
                      item={item}
                      value={
                        <BadgeObservationStatus
                          status={item?.observationStatus || null}
                        />
                      }
                    />
                  </TableRowItem>
                  <TableRowItem className="align-middle">
                    <RenderCell
                      item={item}
                      value={
                        <BadgePerilakuKerja
                          colorNotFinished="neutral"
                          status={item?.perilakuKerjaStatus || null}
                        />
                      }
                    />
                  </TableRowItem>
                  <TableRowItem className="overflow-wrap-anywhere align-middle">
                    {toggleValue?.[
                      'kinerja-dinas-rincian-pelaksanaan-enabled'
                    ] && (
                      <Link href={getCheckUrl(item)} passHref>
                        <Button as="a" size="sm" color="black">
                          Cek
                        </Button>
                      </Link>
                    )}
                  </TableRowItem>
                </TableRow>
              )
            })}
        </TableBody>
      </TablePelaksanaanKinerja>

      {/* Pagination */}
      {hasPelaksanaanData && (
        <Pagination
          totalCount={metas?.total}
          pageSize={metas?.pageSize}
          currentPage={metas?.page}
          showTotal={(totalCount, startRange, endRange) => {
            if (isLoading) return 'Memuat data...'

            return !!totalCount
              ? `Menampilkan ${startRange} – ${endRange} dari ${totalCount} data`
              : 'Tidak ada data'
          }}
          onPageChange={(page) => {
            setPage(page)
          }}
        />
      )}
    </div>
  )
}
export default CardProgresPelaksanaan
